import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Heading, Segmented, Select, Spinner, Stack, Text, TextLink } from '@ui'
import { getJSON } from '../api.js'
import { useAuth } from '../auth.jsx'
import BulkUpload from '../components/BulkUpload.jsx'
import PhotoMap, { PlacePicker, placeGroups, placeOf } from '../components/PhotoMap.jsx'
import { PhotoMonths, PhotoViewer } from '../components/Photos.jsx'
import { SharePhotos } from '../components/ShareControl.jsx'
import { ukDateKey, ukYear } from '../format.js'

// "514 photos", "514 photos and 2 videos"
function countLabel(items) {
  const videos = items.filter((p) => p.media_type === 'video').length
  const photos = items.length - videos
  const n = (k, word) => `${k.toLocaleString('en-GB')} ${word}${k === 1 ? '' : 's'}`
  return videos ? `${n(photos, 'photo')} and ${n(videos, 'video')}` : n(photos, 'photo')
}

const VIEWS = [{ value: 'date', label: 'By date' }, { value: 'map', label: 'On the map' }]

// Every photo, newest first, grouped by month and then by walk, or on a map (?view=map). A
// thumbnail opens the full-screen viewer, which steps through the whole (filtered) list and links
// to each photo's walk. "Go to a place" (right of the Share button) picks a county or district:
// the map moves to its photos and lists them (from the date view it switches to the map).
export default function Photos() {
  const [photos, setPhotos] = useState(null)
  const [error, setError] = useState(null)
  const [year, setYear] = useState('all')
  const [index, setIndex] = useState(null)
  const { admin } = useAuth()
  const [params, setParams] = useSearchParams()
  const view = params.get('view') === 'map' ? 'map' : 'date'
  const setView = (v) => setParams((p) => {
    if (v === 'map') p.set('view', 'map')
    else p.delete('view')
    return p
  }, { replace: true })
  // id → { lat, lon, from, county, district }, fetched with the page (the place list needs it in
  // both views), again after a time changes.
  const [places, setPlaces] = useState(null)
  const loadPlaces = () => getJSON('/api/photos/places')
    .then((rows) => setPlaces(new Map(rows.map((r) => [r.id, r]))))
    .catch((err) => setError(err.message))
  useEffect(() => { loadPlaces() }, [])
  const [place, setPlace] = useState('')
  const [focus, setFocus] = useState(null) // for PhotoMap: { key, ids, label }

  useEffect(() => {
    const controller = new AbortController()
    getJSON('/api/photos', { signal: controller.signal })
      .then(setPhotos)
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message) })
    return () => controller.abort()
  }, [])
  const reload = () => getJSON('/api/photos').then(setPhotos).catch(() => {})

  const years = useMemo(
    () => (photos ? [...new Set(photos.map((p) => ukYear(p.start_time)))].sort((a, b) => b - a) : []),
    [photos],
  )
  const shown = useMemo(
    () => (photos ?? []).filter((p) => year === 'all' || ukYear(p.start_time) === Number(year)),
    [photos, year],
  )

  // "Share these photos…" starts from the year shown, else from the first photo walk to today.
  const today = ukDateKey(new Date().toISOString())
  const shareFrom = year === 'all' ? (photos?.length ? ukDateKey(photos.at(-1).start_time) : today) : `${year}-01-01`
  const shareTo = year === 'all' ? today : `${year}-12-31`

  const groups = useMemo(() => (places ? placeGroups(shown, places) : []), [shown, places])
  const goTo = (value) => {
    setPlace(value)
    const target = value ? placeOf(groups, value) : null
    setFocus({ key: `${value}@${Date.now()}`, ids: target?.ids ?? null, label: target?.label })
    if (value && view !== 'map') setView('map')
  }

  if (error) return <Alert variant="danger">Couldn't load photos: {error}</Alert>
  if (!photos) return <Spinner />

  return (
    <>
      <Stack wrap align="center" gap={2} mb={2}>
        <Heading size={3} mb={0} me="auto">Photos</Heading>
        <Segmented name="photos-view" size="sm" value={view} onChange={setView} options={VIEWS} />
        <Select aria-label="Year" value={year} onChange={(e) => { setYear(e.target.value); setIndex(null); setPlace('') }} style={{ width: 'auto' }}>
          <option value="all">All years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </Select>
        <BulkUpload photos={photos} onAdded={reload} />
      </Stack>
      <Text as="p" muted text="small">
        {countLabel(shown)} from {new Set(shown.map((p) => p.walk_id)).size} walks.
        {photos.length === 0 && ' None uploaded yet.'}
        {admin && <> <TextLink to="/photos/deleted" linkTone="muted">Deleted photos</TextLink></>}
      </Text>

      <SharePhotos
        initialFrom={shareFrom} initialTo={shareTo}
        aside={groups.length > 0 && <PlacePicker groups={groups} value={place} onChange={goTo} />}
      />

      {view === 'map' && (places
        ? (
          <PhotoMap
            photos={shown} places={places} resetKey={year} focus={focus}
            onDeleted={(gone) => setPhotos((all) => all.filter((p) => p.id !== gone))}
            onUpdated={(changed, fields) => {
              setPhotos((all) => all.map((p) => (p.id === changed ? { ...p, ...fields } : p)))
              if ('taken_at' in fields) loadPlaces()
            }}
          />
        )
        : <Spinner />)}

      {view === 'date' && <PhotoMonths photos={shown} onOpen={setIndex} />}

      {view === 'date' && <PhotoViewer
        photos={shown} index={index} onIndex={setIndex} showWalk
        onDeleted={(gone) => {
          setPhotos((all) => all.filter((p) => p.id !== gone))
          setIndex(shown.length > 1 ? Math.min(index, shown.length - 2) : null)
        }}
        onUpdated={(changed, fields) => setPhotos((all) => all.map((p) => (p.id === changed ? { ...p, ...fields } : p)))}
      />}
    </>
  )
}
